import type { ApiResponse } from "@kuroo/contracts";
import { KurooApiError } from "./errors";

interface SdkTransportOptions {
  baseUrl: string;
  fetchImpl: typeof fetch;
  credentials: RequestCredentials;
  provisioningLocalToken?: string;
  organizationId: () => string | undefined;
}

export function createSdkTransport(options: SdkTransportOptions) {
  const organizationHeaders = (input?: HeadersInit): Headers => {
    const headers = new Headers(input ?? {});
    const organizationId = options.organizationId();
    if (organizationId) headers.set("X-Kuroo-Organization-Id", organizationId);
    return headers;
  };

  const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    let response: Response;
    try {
      const headers = organizationHeaders(init.headers);
      if (!headers.has("content-type")) headers.set("content-type", "application/json");
      if (options.provisioningLocalToken && path.startsWith("/api/v1/internal/provisioning")) {
        headers.set("X-Kuroo-Provisioning-Local-Token", options.provisioningLocalToken);
      }
      response = await options.fetchImpl(`${options.baseUrl}${path}`, {
        ...init,
        credentials: options.credentials,
        headers
      });
    } catch (reason) {
      // Browser fetch errors are intentionally vague (usually just
      // "Failed to fetch"). Keep the public SDK contract typed and preserve
      // the original abort semantics for callers that cancel stale requests.
      if (reason instanceof Error && reason.name === "AbortError") {
        throw new KurooApiError("REQUEST_ABORTED", "La solicitud fue cancelada.");
      }
      throw new KurooApiError("NETWORK_ERROR", "No se pudo conectar con la API de Kuroo. Verificá la URL del servicio y tu conexión.");
    }
    let payload: ApiResponse<T>;
    try {
      payload = (await response.json()) as ApiResponse<T>;
    } catch {
      throw new KurooApiError("INVALID_API_RESPONSE", "La API devolvió una respuesta no válida. Revisá que el entorno esté configurado correctamente.", response.headers.get("x-request-id") ?? undefined);
    }
    if (!response.ok || "error" in payload) {
      const error = "error" in payload ? payload.error : { code: "HTTP_ERROR", message: response.statusText };
      throw new KurooApiError(error.code, error.message, error.requestId, error.details);
    }
    return payload.data;
  };

  /** SCIM uses a bare RFC 7644 response rather than the Kuroo REST envelope. */
  const requestScim = async <T>(path: string, token: string, init: RequestInit = {}): Promise<T> => {
    const headers = new Headers(init.headers ?? {});
    headers.set("authorization", `Bearer ${token}`);
    if (init.body !== undefined && !headers.has("content-type")) headers.set("content-type", "application/scim+json");
    const response = await options.fetchImpl(`${options.baseUrl}${path}`, {
      ...init,
      credentials: "omit",
      headers
    });
    const requestId = response.headers.get("x-request-id") ?? undefined;
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    let payload: unknown;
    try { payload = text ? JSON.parse(text) : undefined; } catch { payload = undefined; }
    if (!response.ok) {
      const record = payload && typeof payload === "object" && !Array.isArray(payload) ? payload as Record<string, unknown> : {};
      const scimType = typeof record.scimType === "string" ? record.scimType : "error";
      const detail = typeof record.detail === "string" ? record.detail : response.statusText || "SCIM request failed";
      throw new KurooApiError(`SCIM_${scimType.toUpperCase()}`, detail, requestId);
    }
    return payload as T;
  };

  return { request, requestScim, organizationHeaders };
}
